import React, { useState, useEffect } from 'react'
import {
  Container,
  Header,
  SpaceBetween,
  Grid,
  Box,
  StatusIndicator,
  ColumnLayout,
  Cards,
  Badge,
  Button,
  Alert
} from '@cloudscape-design/components'
import { apiService } from '../services/api'
import { useRole } from '../contexts/RoleContext'
import { RoleBasedContent } from '../components/RoleBasedContent'

interface SystemStats {
  totalUsers: number
  totalDevices: number
  totalLocations: number
  dataProducts: Array<{
    name: string
    status: 'active' | 'inactive' | 'error'
    lastUpdate: string
    locationCount: number
  }>
}

interface UserStats {
  myDevices: number
  myLocationLists: number
  licensedProducts: string[]
  expiringSubscriptions: Array<{
    productName: string
    expirationDate: string
    daysRemaining: number
  }>
}

const Dashboard: React.FC = () => {
  const { isAdmin, isProduction, isStandardUser, isLoading: roleLoading } = useRole()
  const [stats, setStats] = useState<SystemStats>({
    totalUsers: 0,
    totalDevices: 0,
    totalLocations: 0,
    dataProducts: []
  })
  const [userStats, setUserStats] = useState<UserStats>({
    myDevices: 0,
    myLocationLists: 0,
    licensedProducts: [],
    expiringSubscriptions: []
  })
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState<string | null>(null)
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null)

  useEffect(() => {
    if (roleLoading) return;
    if (isAdmin() || isProduction()) {
      loadDashboardData()
    }
    if (isStandardUser()) {
      loadUserStats()
    }
  }, [roleLoading])

  const loadUserStats = async () => {
    try {
      // TODO: Call API to load user-specific stats
      // const devicesResponse = await apiService.get('/api/user/devices');
      // const locationListsResponse = await apiService.get('/api/user/location-lists');
      // const subscriptionsResponse = await apiService.get('/api/user/subscriptions');
      
      // For now, use mock data
      await new Promise(resolve => setTimeout(resolve, 500))
      
      const mockUserStats: UserStats = {
        myDevices: 3,
        myLocationLists: 5,
        licensedProducts: ['METAR Weather', 'Marine Conditions'],
        expiringSubscriptions: [
          {
            productName: 'METAR Weather',
            expirationDate: '2026-03-18',
            daysRemaining: 7
          }
        ]
      }
      
      setUserStats(mockUserStats)
    } catch (error) {
      console.error('Error loading user stats:', error)
    }
  }

  const loadDashboardData = async () => {
    setLoading(true)
    try {
      // Fire ALL requests in parallel — admin counts and all 5 diagnostics
      const dataProducts = ['metar', 'marine', 'trail', 'nws', 'hf_propagation', 'beach_conditions']

      const [countsResponse, ...diagnosticsResponses] = await Promise.all([
        apiService.getAdminCounts(),
        ...dataProducts.map(product => apiService.getDiagnostics(product, true))
      ])

      const totalUsers = (countsResponse.data as any)?.total_users || 0
      const totalDevices = (countsResponse.data as any)?.total_devices || 0

      const dataProductStats = []
      let totalLocations = 0

      for (let i = 0; i < dataProducts.length; i++) {
        const product = dataProducts[i]
        try {
          const diagnosticsResponse = diagnosticsResponses[i]
          const diagnostics = diagnosticsResponse.data
          const locationStats = diagnostics?.location_statistics
          const locationCount = locationStats?.total_locations || 0
          totalLocations += locationCount

          const lastCollectionTime = diagnostics?.last_collection_time
          
          let lastUpdate = 'Never'
          if (lastCollectionTime) {
            const lastTime = new Date(lastCollectionTime)
            const now = new Date()
            const diffMinutes = Math.floor((now.getTime() - lastTime.getTime()) / (1000 * 60))
            
            if (diffMinutes < 60) {
              lastUpdate = `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`
            } else if (diffMinutes < 1440) {
              const hours = Math.floor(diffMinutes / 60)
              lastUpdate = `${hours} hour${hours !== 1 ? 's' : ''} ago`
            } else {
              const days = Math.floor(diffMinutes / 1440)
              lastUpdate = `${days} day${days !== 1 ? 's' : ''} ago`
            }
          }

          let status: 'active' | 'inactive' | 'error' = 'inactive'
          if (locationCount > 0 && lastCollectionTime) {
            status = 'active'
          } else if (locationCount === 0) {
            status = 'inactive'
          }

          dataProductStats.push({
            name: getDataProductName(product),
            status,
            lastUpdate,
            locationCount
          })
        } catch (error) {
          console.error(`Error loading ${product} diagnostics:`, error)
          dataProductStats.push({
            name: getDataProductName(product),
            status: 'error' as const,
            lastUpdate: 'Error',
            locationCount: 0
          })
        }
      }

      setStats({
        totalUsers,
        totalDevices,
        totalLocations,
        dataProducts: dataProductStats
      })
    } catch (error) {
      console.error('Error loading dashboard data:', error)
      setAlert({
        type: 'error',
        message: 'Failed to load dashboard data. Please try again.'
      })
    } finally {
      setLoading(false)
    }
  }

  const getDataProductName = (product: string): string => {
    switch (product) {
      case 'metar': return 'METAR Weather'
      case 'marine': return 'Marine Conditions'
      case 'trail': return 'Trail Conditions'
      case 'nws': return 'NWS Observations'
      case 'hf_propagation': return 'HF Radio Propagation'
      default: return product
    }
  }

  const getDataProductKey = (name: string): string => {
    switch (name) {
      case 'METAR Weather': return 'metar'
      case 'Marine Conditions': return 'marine'
      case 'Trail Conditions': return 'trail'
      case 'NWS Observations': return 'nws'
      case 'HF Radio Propagation': return 'hf_propagation'
      default: return name.toLowerCase()
    }
  }

  const handleRefreshData = async (dataProductName: string) => {
    const dataProduct = getDataProductKey(dataProductName)
    setRefreshing(dataProduct)
    setAlert(null)

    try {
      const response = await apiService.triggerDataCollection(dataProduct)
      
      if (response.statusCode === 200) {
        setAlert({
          type: 'success',
          message: `Data collection triggered for ${dataProductName}. Refresh will complete in a few minutes.`
        })
        
        // Reload dashboard data after a short delay
        setTimeout(() => {
          loadDashboardData()
        }, 2000)
      } else {
        throw new Error(response.error || 'Failed to trigger data collection')
      }
    } catch (error) {
      console.error('Error triggering data collection:', error)
      setAlert({
        type: 'error',
        message: `Failed to refresh ${dataProductName}. Please try again.`
      })
    } finally {
      setRefreshing(null)
    }
  }

  const getStatusIndicator = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusIndicator type="success">Active</StatusIndicator>
      case 'inactive':
        return <StatusIndicator type="stopped">Inactive</StatusIndicator>
      case 'error':
        return <StatusIndicator type="error">Error</StatusIndicator>
      default:
        return <StatusIndicator type="pending">Unknown</StatusIndicator>
    }
  }

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Dashboard</Header>
      
      {alert && (
        <Alert
          type={alert.type}
          dismissible
          onDismiss={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}
      
      {/* System Metrics for Admin/Production */}
      <RoleBasedContent requiredRole={['Admin', 'Production']}>
        <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
          <Container header={<Header variant="h2">Users</Header>}>
            <Box textAlign="center">
              <Box variant="h1" color="text-status-info">
                {loading ? '...' : stats.totalUsers}
              </Box>
              <Box variant="small">Total registered users</Box>
            </Box>
          </Container>
          
          <Container header={<Header variant="h2">Devices</Header>}>
            <Box textAlign="center">
              <Box variant="h1" color="text-status-success">
                {loading ? '...' : stats.totalDevices}
              </Box>
              <Box variant="small">Active devices</Box>
            </Box>
          </Container>
          
          <Container header={<Header variant="h2">Locations</Header>}>
            <Box textAlign="center">
              <Box variant="h1" color="text-status-warning">
                {loading ? '...' : stats.totalLocations}
              </Box>
              <Box variant="small">Total data locations</Box>
            </Box>
          </Container>
        </Grid>
      </RoleBasedContent>
      
      {/* User Metrics for Standard Users */}
      <RoleBasedContent requiredRole="User">
        <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
          <Container header={<Header variant="h2">My Devices</Header>}>
            <Box textAlign="center">
              <Box variant="h1" color="text-status-success">
                {loading ? '...' : userStats.myDevices}
              </Box>
              <Box variant="small">Registered devices</Box>
            </Box>
          </Container>
          
          <Container header={<Header variant="h2">My Location Lists</Header>}>
            <Box textAlign="center">
              <Box variant="h1" color="text-status-info">
                {loading ? '...' : userStats.myLocationLists}
              </Box>
              <Box variant="small">Location lists</Box>
            </Box>
          </Container>
          
          <Container header={<Header variant="h2">Licensed Products</Header>}>
            <Box textAlign="center">
              <Box variant="h1" color="text-status-warning">
                {loading ? '...' : userStats.licensedProducts.length}
              </Box>
              <Box variant="small">Active subscriptions</Box>
            </Box>
          </Container>
        </Grid>
        
        {/* Subscription Expiration Warnings */}
        {userStats.expiringSubscriptions.length > 0 && (
          <Alert type="warning">
            <SpaceBetween size="xs">
              <Box variant="strong">Subscriptions Expiring Soon</Box>
              {userStats.expiringSubscriptions.map((sub, index) => (
                <Box key={index}>
                  {sub.productName} expires in {sub.daysRemaining} days ({new Date(sub.expirationDate).toLocaleDateString()})
                </Box>
              ))}
              <Button href="/orders">Renew Subscriptions</Button>
            </SpaceBetween>
          </Alert>
        )}
      </RoleBasedContent>

      {/* Data Products Status - Admin/Production only */}
      <RoleBasedContent requiredRole={['Admin', 'Production']}>
        <Container 
          header={
            <Header 
              variant="h2"
              actions={
                <Button 
                  onClick={loadDashboardData}
                  loading={loading}
                >
                  Refresh All
                </Button>
              }
            >
              Data Products Status
            </Header>
          }
        >
        <Cards
          cardDefinition={{
            header: item => item.name,
            sections: [
              {
                id: 'status',
                header: 'Status',
                content: item => getStatusIndicator(item.status)
              },
              {
                id: 'lastUpdate',
                header: 'Last Update',
                content: item => item.lastUpdate
              },
              {
                id: 'locations',
                header: 'Locations',
                content: item => (
                  <Badge color="blue">{item.locationCount} locations</Badge>
                )
              },
              {
                id: 'actions',
                header: 'Actions',
                content: item => (
                  <Button
                    variant="primary"
                    loading={refreshing === getDataProductKey(item.name)}
                    onClick={() => handleRefreshData(item.name)}
                  >
                    Refresh Data
                  </Button>
                )
              }
            ]
          }}
          cardsPerRow={[
            { cards: 1 },
            { minWidth: 500, cards: 2 },
            { minWidth: 800, cards: 3 }
          ]}
          items={stats.dataProducts}
          loading={loading}
          empty={
            <Box textAlign="center" color="inherit">
              <b>No data products</b>
              <Box variant="p" color="inherit">
                No data products are currently configured.
              </Box>
            </Box>
          }
        />
      </Container>
      </RoleBasedContent>
    </SpaceBetween>
  )
}

export default Dashboard